import { useFormikContext } from 'formik';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SquareFormValues } from '@/utils/forms/squareTransformers';
import { getPeriodTemplates, type PeriodTemplate } from '@/utils/squares/periodTemplates';
import { useEffect, useMemo, useState } from 'react';
import { Save, Copy, X } from 'lucide-react';
import { toast } from 'sonner';
import { useSubmitManualScoresMutation } from '@/api/squares/submitManualScores';
import { useGetPeriodResultsQuery } from '@/api/squares/getPeriodResults';
import { useGetFixtureScoresQuery } from '@/api/squares/getFixtureScores';
import { Loader2, Trophy, RefreshCw } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { LiveScoringSection } from './LiveScoringSection';
import { InfoIconDialog } from '@/components/ui/info-icon-dialog';

interface ScoringSectionProps {
  disabled?: boolean;
  squareId?: string;
}

interface PeriodScore {
  periodCode: string;
  rowTeamScore: number | '';
  colTeamScore: number | '';
}

export function ScoringSection({ disabled = false, squareId }: ScoringSectionProps) {
  const { values } = useFormikContext<SquareFormValues>();
  const [periodScores, setPeriodScores] = useState<Record<string, PeriodScore>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  /** When set, this period's scores are in edit mode (editable inputs); otherwise graded periods show read-only with Edit button */
  const [editingPeriodCode, setEditingPeriodCode] = useState<string | null>(null);

  // For live scoring, use squareId which can be either question_id or squares_contest_id
  // The backend will handle both cases
  const questionIdForLiveScoring = squareId || null;

  // Get period results to display winners
  const { data: periodResultsData, isLoading: resultsLoading } = useGetPeriodResultsQuery(
    squareId || null
  );

  // Fixture scores for "Copy from feed" (live or final scores)
  const { data: fixtureScoresData } = useGetFixtureScoresQuery(squareId || null);

  // Get periods from form values
  const periods = useMemo(() => {
    if (!values.periods || values.periods.length === 0) {
      // If no periods set, get templates based on sport
      if (values.sport) {
        return getPeriodTemplates(values.sport);
      }
      return [];
    }
    return values.periods as PeriodTemplate[];
  }, [values.periods, values.sport]);

  // Initialize period scores from form
  useEffect(() => {
    if (periods.length > 0 && Object.keys(periodScores).length === 0) {
      const initialScores: Record<string, PeriodScore> = {};
      periods.forEach(period => {
        const existingResult = periodResultsData?.periodResults?.find(
          pr => pr.periodCode === period.periodCode
        );
        initialScores[period.periodCode] = {
          periodCode: period.periodCode,
          // If the contest was already manually scored, rehydrate inputs from saved period results
          rowTeamScore: existingResult?.rowTeamScore ?? '',
          colTeamScore: existingResult?.colTeamScore ?? '',
        };
      });
      setPeriodScores(initialScores);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periods, periodResultsData]);

  // If you navigate away and come back, we also want to populate any still-empty fields
  // from saved period results without overwriting fields the admin is actively editing.
  useEffect(() => {
    const saved = periodResultsData?.periodResults || [];
    if (saved.length === 0) return;

    setPeriodScores(prev => {
      if (!prev || Object.keys(prev).length === 0) return prev;

      let changed = false;
      const next: Record<string, PeriodScore> = { ...prev };
      for (const pr of saved) {
        const existing = next[pr.periodCode];
        if (!existing) continue;

        if (
          existing.rowTeamScore === '' &&
          pr.rowTeamScore !== null &&
          pr.rowTeamScore !== undefined
        ) {
          next[pr.periodCode] = { ...next[pr.periodCode], rowTeamScore: pr.rowTeamScore };
          changed = true;
        }
        if (
          existing.colTeamScore === '' &&
          pr.colTeamScore !== null &&
          pr.colTeamScore !== undefined
        ) {
          next[pr.periodCode] = { ...next[pr.periodCode], colTeamScore: pr.colTeamScore };
          changed = true;
        }
      }

      return changed ? next : prev;
    });
  }, [periodResultsData]);

  const handleScoreChange = (
    periodCode: string,
    field: 'rowTeamScore' | 'colTeamScore',
    value: string
  ) => {
    setPeriodScores(prev => ({
      ...prev,
      [periodCode]: {
        ...prev[periodCode],
        periodCode,
        [field]: value === '' ? '' : parseInt(value, 10) || '',
      },
    }));
  };

  /** Map fixture feed scores to row/col using contest team IDs; fill periodScores for Copy from feed */
  const handleCopyFromFeed = () => {
    const fixture = fixtureScoresData?.fixture;
    if (!fixture || !periods.length) {
      toast.error('No fixture scores available or no periods configured');
      return;
    }
    const homeId = fixture.homeTeam?.id;
    const awayId = fixture.awayTeam?.id;
    const rowTeamId = values.row_team_id;
    if (!homeId || !awayId) {
      toast.error('Fixture team info missing');
      return;
    }
    // Row/col map: contest row team = one of home/away, col = the other
    const rowIsHome = rowTeamId === homeId;
    const periodScoresFromFeed = fixture.periodScores;
    const quarterScores =
      periodScoresFromFeed
        ?.filter((p: { periodType?: string }) => p.periodType === 'quarter')
        .slice()
        .sort(
          (a: { periodNumber: number }, b: { periodNumber: number }) =>
            a.periodNumber - b.periodNumber
        ) || [];
    const halfScores =
      periodScoresFromFeed
        ?.filter((p: { periodType?: string }) => p.periodType === 'half')
        .slice()
        .sort(
          (a: { periodNumber: number }, b: { periodNumber: number }) =>
            a.periodNumber - b.periodNumber
        ) || [];
    let cumHome = 0;
    let cumAway = 0;
    const cumulativeByQuarter = new Map<number, { home: number; away: number }>();
    for (const p of quarterScores) {
      cumHome += p.homeScore || 0;
      cumAway += p.awayScore || 0;
      cumulativeByQuarter.set(p.periodNumber, { home: cumHome, away: cumAway });
    }
    let cumHalfHome = 0;
    let cumHalfAway = 0;
    const cumulativeByHalf = new Map<number, { home: number; away: number }>();
    for (const p of halfScores) {
      cumHalfHome += p.homeScore || 0;
      cumHalfAway += p.awayScore || 0;
      cumulativeByHalf.set(p.periodNumber, { home: cumHalfHome, away: cumHalfAway });
    }
    const finalHome = fixture.homeScore ?? cumHome;
    const finalAway = fixture.awayScore ?? cumAway;

    const toRowCol = (home: number, away: number) =>
      rowIsHome ? { row: home, col: away } : { row: away, col: home };

    const next: Record<string, PeriodScore> = {};
    for (const period of periods) {
      const code = period.periodCode.toUpperCase();
      let home = 0;
      let away = 0;
      if (code === 'FINAL' || code === 'END_OF_GAME') {
        home = finalHome;
        away = finalAway;
      } else if (code === 'HT' || code === 'HALFTIME') {
        if (cumulativeByHalf.size > 0) {
          const h1 = cumulativeByHalf.get(1);
          if (h1) {
            home = h1.home;
            away = h1.away;
          } else {
            home = cumulativeByQuarter.get(2)?.home ?? 0;
            away = cumulativeByQuarter.get(2)?.away ?? 0;
          }
        } else {
          home = cumulativeByQuarter.get(2)?.home ?? 0;
          away = cumulativeByQuarter.get(2)?.away ?? 0;
        }
      } else if (code.startsWith('Q')) {
        const q = parseInt(code.replace('Q', ''), 10);
        const c = cumulativeByQuarter.get(q);
        if (c) {
          home = c.home;
          away = c.away;
        }
      } else if (code.startsWith('P')) {
        const p = parseInt(code.replace('P', ''), 10);
        const c = cumulativeByQuarter.get(p) ?? cumulativeByHalf.get(p);
        if (c) {
          home = c.home;
          away = c.away;
        }
      }
      const { row, col } = toRowCol(home, away);
      next[period.periodCode] = {
        periodCode: period.periodCode,
        rowTeamScore: row,
        colTeamScore: col,
      };
    }
    setPeriodScores(next);
    toast.success('Scores copied from feed. Edit if needed and click Save Scores & Grade Contest.');
  };

  const submitManualScoresMutation = useSubmitManualScoresMutation();

  const handleSaveScores = async () => {
    if (!squareId) {
      toast.error('Contest ID is required');
      return;
    }

    // Prepare scores array - only include periods that have both scores entered
    const scoresToSave = Object.values(periodScores).filter(
      score => score && score.rowTeamScore !== '' && score.colTeamScore !== ''
    );

    if (scoresToSave.length === 0) {
      toast.error('Please enter at least one period score to save');
      return;
    }

    // Validate scores have valid numbers
    const scores = scoresToSave
      .map(score => ({
        periodCode: score.periodCode,
        rowTeamScore: Number(score.rowTeamScore),
        colTeamScore: Number(score.colTeamScore),
      }))
      .filter(score => !isNaN(score.rowTeamScore) && !isNaN(score.colTeamScore));

    if (scores.length === 0) {
      toast.error('Please enter valid scores');
      return;
    }

    setIsSubmitting(true);
    try {
      await submitManualScoresMutation.mutateAsync({
        contestId: squareId,
        scores,
      });
      setEditingPeriodCode(null);
    } catch (error) {
      console.error('Failed to save scores:', error);
      // Error toast is handled by the mutation
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!values.sport || periods.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold">Manual Scoring</h3>
          <InfoIconDialog title="Manual Scoring" ariaLabel="Manual scoring information">
            {`Manually enter scores for each period to grade the contest.

Please select a sport and configure periods first.`}
          </InfoIconDialog>
        </div>
        <div className="text-center py-8 text-muted-foreground">
          <p>Please select a sport and configure periods first</p>
        </div>
      </div>
    );
  }

  const hasAnyScores = Object.values(periodScores).some(
    score => score && score.rowTeamScore !== '' && score.colTeamScore !== ''
  );
  const periodResults = periodResultsData?.periodResults || [];

  return (
    <div className="space-y-6">
      {/* Live Auto Scoring Section */}
      <LiveScoringSection
        questionId={questionIdForLiveScoring}
        disabled={disabled}
        rowTeamIconUrl={values.row_team_icon_url || undefined}
        colTeamIconUrl={values.col_team_icon_url || undefined}
        sport={values.sport || undefined}
      />

      {/* Manual Scoring Section - works during and after live; re-grades without disrupting auto */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-semibold">Manual Score (Edit / Override)</h3>
          <InfoIconDialog
            title="Manual Score (Edit / Override)"
            ariaLabel="Manual score edit information"
          >
            {`During or after a live game you can adjust any period score (including already auto-graded). For graded periods, click Edit to show the score inputs, change the scores, then click Save & Update to re-grade that period. Live auto-scoring is not disrupted. Use "Load graded scores" or "Copy from feed" to pre-fill.`}
          </InfoIconDialog>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              const saved = periodResultsData?.periodResults || [];
              if (saved.length === 0) {
                toast.info('No graded period results to load');
                return;
              }
              const next: Record<string, PeriodScore> = {};
              periods.forEach(period => {
                const pr = saved.find(p => p.periodCode === period.periodCode);
                next[period.periodCode] = {
                  periodCode: period.periodCode,
                  rowTeamScore: pr?.rowTeamScore ?? '',
                  colTeamScore: pr?.colTeamScore ?? '',
                };
              });
              setPeriodScores(next);
              toast.success('Loaded current graded scores. Edit and save to re-grade.');
            }}
            disabled={disabled || !squareId || periods.length === 0}
            className="gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            Load graded scores
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleCopyFromFeed}
            disabled={disabled || !squareId || !fixtureScoresData?.fixture || periods.length === 0}
            className="gap-2"
          >
            <Copy className="h-4 w-4" />
            Copy from feed
          </Button>
        </div>
      </div>

      <div className="space-y-4">
        {periods.map(period => {
          const score = periodScores[period.periodCode] || {
            periodCode: period.periodCode,
            rowTeamScore: '',
            colTeamScore: '',
          };

          const periodResult = periodResults.find(pr => pr.periodCode === period.periodCode);

          const isEditingThisPeriod = editingPeriodCode === period.periodCode;
          const showReadOnly = periodResult && !isEditingThisPeriod;

          const handleStartEdit = () => {
            if (periodResult && (score.rowTeamScore === '' || score.colTeamScore === '')) {
              setPeriodScores(prev => ({
                ...prev,
                [period.periodCode]: {
                  periodCode: period.periodCode,
                  rowTeamScore: periodResult.rowTeamScore ?? '',
                  colTeamScore: periodResult.colTeamScore ?? '',
                },
              }));
            }
            setEditingPeriodCode(period.periodCode);
          };

          return (
            <div key={period.periodCode} className="border rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium">{period.label || period.periodCode}</h4>
                  <p className="text-xs text-muted-foreground">Period Code: {period.periodCode}</p>
                </div>
                <div className="flex items-center gap-2">
                  {periodResult && (
                    <Badge variant="success" className="bg-green-100 text-green-800">
                      <Trophy className="w-3 h-3 mr-1" />
                      Graded
                    </Badge>
                  )}
                  {showReadOnly ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={handleStartEdit}
                      disabled={disabled}
                      aria-label="Edit score"
                      className="shrink-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none"
                    >
                      <img src="/PencilSimple.svg" alt="" className="h-4 w-4" aria-hidden />
                    </Button>
                  ) : periodResult && isEditingThisPeriod ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => setEditingPeriodCode(null)}
                      disabled={disabled}
                      className="gap-1.5"
                    >
                      <X className="h-3.5 w-3.5" />
                      Cancel
                    </Button>
                  ) : null}
                </div>
              </div>

              {showReadOnly ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">
                      {values.row_team_display_name || 'Row Team'} Score
                    </p>
                    <p className="text-lg font-semibold">{periodResult?.rowTeamScore ?? '—'}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Last digit: {periodResult?.rowLastDigit ?? '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">
                      {values.col_team_display_name || 'Column Team'} Score
                    </p>
                    <p className="text-lg font-semibold">{periodResult?.colTeamScore ?? '—'}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Last digit: {periodResult?.colLastDigit ?? '—'}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor={`${period.periodCode}-row-score`}>
                      {values.row_team_display_name || 'Row Team'} Score
                    </Label>
                    <Input
                      id={`${period.periodCode}-row-score`}
                      type="number"
                      min="0"
                      value={score.rowTeamScore}
                      // Blur on wheel so page scroll doesn't silently change the score
                      onWheel={e => (e.currentTarget as HTMLInputElement).blur()}
                      onChange={e =>
                        handleScoreChange(period.periodCode, 'rowTeamScore', e.target.value)
                      }
                      disabled={disabled}
                      placeholder="0"
                    />
                    {periodResult && (
                      <p className="text-xs text-muted-foreground">
                        Last digit: {periodResult.rowLastDigit}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label htmlFor={`${period.periodCode}-col-score`}>
                      {values.col_team_display_name || 'Column Team'} Score
                    </Label>
                    <Input
                      id={`${period.periodCode}-col-score`}
                      type="number"
                      min="0"
                      value={score.colTeamScore}
                      // Blur on wheel so page scroll doesn't silently change the score
                      onWheel={e => (e.currentTarget as HTMLInputElement).blur()}
                      onChange={e =>
                        handleScoreChange(period.periodCode, 'colTeamScore', e.target.value)
                      }
                      disabled={disabled}
                      placeholder="0"
                    />
                    {periodResult && (
                      <p className="text-xs text-muted-foreground">
                        Last digit: {periodResult.colLastDigit}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {periodResult && (
                <div className="mt-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <p className="text-sm font-medium mb-2">
                    Winning Square #{periodResult.winningSquareNumber}
                  </p>
                  {periodResult.winners && periodResult.winners.length > 0 ? (
                    <p className="text-xs text-muted-foreground">
                      {periodResult.winners.length} Winner
                      {periodResult.winners.length !== 1 ? 's' : ''}
                    </p>
                  ) : (
                    <p className="text-xs text-muted-foreground">No winners for this period</p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Save & Update - visible immediately after period cards */}
      <div className="flex flex-wrap items-center justify-between gap-4 py-4 px-4 rounded-lg border-2 border-primary/30 bg-primary/5">
        <InfoIconDialog title="Save scores" ariaLabel="Save scores information">
          After editing scores above, click Save & Update to re-grade the selected periods.
        </InfoIconDialog>
        <Button
          type="button"
          onClick={handleSaveScores}
          disabled={disabled || isSubmitting || !hasAnyScores || !squareId}
          className="gap-2 min-w-[180px]"
        >
          <Save className="h-4 w-4" />
          {isSubmitting
            ? 'Saving & re-grading...'
            : periodResults.length > 0
              ? 'Save & Update'
              : 'Save Scores & Grade Contest'}
        </Button>
      </div>

      {/* Winning Squares Summary - Prominent Display */}
      {periodResults.length > 0 && (
        <div className="border rounded-lg p-6 bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/20 dark:to-blue-900/20">
          <div className="flex items-center gap-2 mb-4">
            <Trophy className="w-6 h-6 text-yellow-500" />
            <h3 className="text-xl font-bold">Winning Squares by Period</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {periodResults.map(result => (
              <div
                key={result.periodCode}
                className="bg-white dark:bg-gray-800 rounded-lg p-4 border-2 border-green-300 dark:border-green-700 shadow-md"
              >
                <div className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">
                  {result.periodCode}
                </div>
                <div className="text-3xl font-bold text-green-600 dark:text-green-400 mb-2">
                  #{result.winningSquareNumber}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                  {result.winners && result.winners.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                      <div className="font-semibold text-green-700 dark:text-green-300">
                        {result.winners.length} Winner{result.winners.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Period Results & Winners table - hidden; same data is on Entries page. Set to true to show. */}
      {(() => {
        const showPeriodResultsTable = false;
        return showPeriodResultsTable && periodResults.length > 0 ? (
          <div className="border rounded-lg p-4 space-y-4">
            <h4 className="text-lg font-semibold flex items-center gap-2">
              <Trophy className="w-5 h-5" />
              Period Results & Winners
            </h4>
            {resultsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : (
              <div className="border rounded-md overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase dark:text-gray-400">
                        Period
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase dark:text-gray-400">
                        Scores (Row, Col)
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase dark:text-gray-400">
                        Winning Square
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase dark:text-gray-400">
                        Winners
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    {periodResults.map(result => {
                      const periodLabel =
                        periods.find(p => p.periodCode === result.periodCode)?.label ||
                        result.periodCode;
                      return (
                        <tr
                          key={result.periodCode}
                          className="hover:bg-gray-50 dark:hover:bg-gray-800"
                        >
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                            {periodLabel}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                            {result.rowTeamScore !== null && result.colTeamScore !== null
                              ? `${result.rowTeamScore} - ${result.colTeamScore}`
                              : 'N/A'}
                            <br />
                            <span className="text-xs text-muted-foreground">
                              (Last digits: {result.rowLastDigit}, {result.colLastDigit})
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                            Square #{result.winningSquareNumber}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                            {result.winners && result.winners.length > 0 ? (
                              <div>
                                <span className="font-medium">
                                  {result.winners.length} winner(s)
                                </span>
                                <ul className="mt-1 list-disc list-inside text-xs text-muted-foreground">
                                  {result.winners.map(winner => (
                                    <li key={winner.userId}>{winner.username}</li>
                                  ))}
                                </ul>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">No winners</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : null;
      })()}

      <div className="flex justify-end pt-4 border-t">
        <Button
          type="button"
          onClick={handleSaveScores}
          disabled={disabled || isSubmitting || !hasAnyScores || !squareId}
          className="gap-2 min-w-[180px]"
        >
          <Save className="h-4 w-4" />
          {isSubmitting
            ? 'Saving & re-grading...'
            : periodResults.length > 0
              ? 'Save & Update'
              : 'Save Scores & Grade Contest'}
        </Button>
      </div>
    </div>
  );
}
